import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Alert, Box, IconButton } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/genesisApi/httpsMethods';
import type { ApiResponse } from '../../services/genesisApi/httpsMethods';
import { CustomButton } from '../common/customButton';
import { ErrorDialog } from '../common/errorDialog';
import { useErrorDialog } from '../../hooks/useErrorDialog';
import { numberFormatter } from '../../utils/numberFormatter';
import type {
  Accordion,
  DealAdvisorFee,
  CreateDealAdvisorFeeRequest,
  Status,
  UpdateDealAdvisorFeeRequest,
} from '../../types/deal';
import { REQUIRED_ADVISOR_SERVICES, statusIsInClosing } from '../../utils/dealValidation';
import DeleteIcon from '@mui/icons-material/Delete';
import { createSingleSelectColumn } from '../../utils/columnHelpers';
import { useReferenceLists } from '../../context/GlobalGetLists';
import { useCurrentUser } from '../../context/CurrentUserContext';
import '../../assets/css/AGGrid-custom.css';
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

interface DealAdvisorFeesProps {
  dealId: number;
  /** The deal's add-ons; fees can be allocated to one of them. */
  accordions?: Accordion[];
  /** The deal's status, for the in-closing quote reminder. */
  dealStatus?: Status | null;
}

type ServiceTypeOption = { id: string; service: string };
type CurrencyOption = { id: string; currency: string };
type AccordionOption = { id: number; accordion_name: string };
type DealAdvisorFeeRow = Omit<DealAdvisorFee, 'id'> & {
  id: number | string;
  accordion?: AccordionOption | null;
  /** Suggested standard service with no row yet; saved once filled in. */
  isPlaceholder?: boolean;
};

const WHOLE_DEAL_LABEL = 'Whole deal';
const NO_ACCORDIONS: Accordion[] = [];

// Keep stable across renders so effects don't re-run and wipe local edits (e.g. Add Row).
const SERVICE_TYPES: ServiceTypeOption[] = [
  { id: 'Insurance', service: 'Insurance' },
  { id: 'Valuation', service: 'Valuation' },
  { id: 'Legal', service: 'Legal' },
  { id: 'Environmental', service: 'Environmental' },
  { id: 'Survey', service: 'Survey' },
  { id: 'Tax Advisory', service: 'Tax Advisory' },
  { id: 'Accounting', service: 'Accounting' },
  { id: 'Technical Due Diligence', service: 'Technical Due Diligence' },
  { id: 'Planning', service: 'Planning' },
  { id: 'Other', service: 'Other' },
];

const sameService = (a: unknown, b: string) =>
  String(a ?? '').trim().toLowerCase() === b.toLowerCase();

const serviceName = (service: unknown): string => {
  if (typeof service === 'string') return service;
  if (service && typeof service === 'object') {
    return String((service as { service?: unknown }).service ?? '');
  }
  return '';
};

const DealAdvisorFees: React.FC<DealAdvisorFeesProps> = ({
  dealId,
  accordions = NO_ACCORDIONS,
  dealStatus = null,
}) => {
  const { canEdit } = useCurrentUser();
  const [fees, setFees] = useState<DealAdvisorFeeRow[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [initialFees, setInitialFees] = useState<DealAdvisorFeeRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const gridRef = useRef<AgGridReact>(null);
  const { currencies: currencyStrings } = useReferenceLists();

  const serviceTypes = SERVICE_TYPES;
  const currencyOptions = useMemo<CurrencyOption[]>(
    () => currencyStrings.map((c: string) => ({ id: c, currency: c })),
    [currencyStrings],
  );
  // The auto-created baseline ("Initial Deal") is the deal itself, not an add-on.
  const accordionOptions = useMemo<AccordionOption[]>(
    () =>
      accordions
        .filter(
          (a) =>
            a.accordion_type !== 'baseline_retrospective' &&
            a.accordion_name !== 'Initial Deal',
        )
        .map((a) => ({ id: a.id, accordion_name: a.accordion_name })),
    [accordions],
  );
  // Slices that get suggested standard-service rows: the deal and its open add-ons.
  const placeholderSlices = useMemo<Array<AccordionOption | null>>(
    () => [
      null,
      ...accordionOptions.filter((option) =>
        accordions.some(
          (a) => a.id === option.id && a.accordion_type === 'pipeline',
        ),
      ),
    ],
    [accordions, accordionOptions],
  );

  const mapApiFeesToRows = useCallback(
    (data: DealAdvisorFee[]): DealAdvisorFeeRow[] => {
      const rows: DealAdvisorFeeRow[] = data.map((fee: any) => {
        const serviceString = typeof fee.service === 'string' ? fee.service : '';
        const serviceObj = serviceTypes.find((s) => s.service === serviceString) ?? null;
        const currencyString = typeof fee.currency === 'string' ? fee.currency : '';
        const currencyObj =
          currencyOptions.find((c) => c.currency === currencyString) ?? null;
        const accordionObj =
          accordionOptions.find((a) => a.id === fee.accordion_id) ??
          (fee.accordion_id != null
            ? { id: fee.accordion_id, accordion_name: fee.accordion_name ?? `Add-on ${fee.accordion_id}` }
            : null);
        return { ...(fee as any), service: serviceObj, currency: currencyObj, accordion: accordionObj };
      });

      const placeholders: DealAdvisorFeeRow[] = [];
      for (const slice of placeholderSlices) {
        for (const service of REQUIRED_ADVISOR_SERVICES) {
          const hasRow = data.some(
            (fee) =>
              (fee.accordion_id ?? null) === (slice?.id ?? null) &&
              sameService(fee.service, service),
          );
          if (hasRow) continue;
          placeholders.push({
            id: `placeholder-${slice?.id ?? 'deal'}-${service}`,
            deal_id: dealId,
            accordion_id: slice?.id ?? null,
            accordion: slice,
            service: (serviceTypes.find((s) => s.service === service) ?? null) as any,
            firm: '',
            task: null,
            currency: null,
            quote_amount: null,
            actual_amount: null,
            billable_hours_total_amount: null,
            created_at: '',
            updated_at: '',
            isPlaceholder: true,
          });
        }
      }
      return [...rows, ...placeholders];
    },
    [accordionOptions, currencyOptions, dealId, placeholderSlices],
  );

  // Fetch fees on mount and when dealId or reference currency list changes
  useEffect(() => {
    const fetchFees = async () => {
      try {
        const response = await apiGet<DealAdvisorFee[]>(`deal-advisor-fees/${dealId}`);
        if (response && response.success && response.data) {
          const rows = mapApiFeesToRows(response.data);
          setFees(rows);
          setInitialFees(structuredClone(rows));
        }
      } catch (error) {
        console.error('Failed to fetch advisor fees:', error);
      }
    };

    if (dealId) {
      fetchFees();
    }
  }, [dealId, mapApiFeesToRows]);

  // Track if grid is dirty
  useEffect(() => {
    const isEqual = JSON.stringify(fees) === JSON.stringify(initialFees);
    setDirtyGrid(!isEqual);
  }, [fees, initialFees]);

  const onCellValueChanged = useCallback((event: any) => {
    const { colDef, newValue, oldValue, data } = event;
    const field = colDef.field;

    // In some cases (notably popup editors), AG Grid can emit onCellValueChanged with `newValue` undefined
    // even though the editor selected a real value. Be defensive and fall back to other event sources.
    const committedValue =
      newValue !== undefined
        ? newValue
        : event?.value !== undefined
          ? event.value
          : data && field && (data as any)[field] !== undefined
            ? (data as any)[field]
            : undefined;

    if (committedValue === undefined) return;
    if (committedValue === oldValue) return;

    setFees((prevFees) =>
      prevFees.map((fee) =>
        fee.id === data.id ? { ...fee, [field]: committedValue } : fee
      )
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    setFees(structuredClone(initialFees));
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // Get changed items
      const changedItems = fees.filter((fee, index) => {
        const initial = initialFees[index];
        if (!initial) return false;
        return JSON.stringify(fee) !== JSON.stringify(initial);
      });

      // Helper to extract string value from service field (in case it's an object)
      const getServiceValue = (service: any): string => {
        if (typeof service === 'string') return service;
        if (service && typeof service === 'object') return service.service || service.label || String(service.id ?? '');
        return service || '';
      };

      const getCurrencyValue = (currency: any): string | null => {
        if (currency == null) return null;
        if (typeof currency === 'string') return currency;
        if (currency && typeof currency === 'object') return currency.currency || currency.label || String(currency.id ?? '');
        return String(currency);
      };

      const accordionIdOf = (fee: DealAdvisorFeeRow): number | null =>
        fee.accordion?.id ?? null;

      // Update existing fees
      const updatePromises = changedItems
        .filter((fee) => fee.id && typeof fee.id === 'number')
        .map((fee) => {
          const updateData: UpdateDealAdvisorFeeRequest = {
            service: getServiceValue(fee.service),
            firm: fee.firm,
            task: fee.task,
            currency: getCurrencyValue(fee.currency),
            quote_amount: fee.quote_amount,
            actual_amount: fee.actual_amount,
            billable_hours_total_amount: fee.billable_hours_total_amount,
            accordion_id: accordionIdOf(fee),
          };
          return apiPut(`deal-advisor-fees`, fee.id as number, updateData).catch(
            (): ApiResponse => ({
              success: false,
              status: 0,
              message: 'Failed to save advisor fee. Please try again.',
            }),
          );
        });

      // Create new fees: added rows (ids starting 'new-') and suggested rows
      // the user filled in. Untouched suggestions are not saved.
      const initialById = new Map(initialFees.map((fee) => [fee.id, fee]));
      const newFees = fees.filter((fee) => {
        if (typeof fee.id === 'number') return false;
        if (fee.isPlaceholder) {
          return JSON.stringify(fee) !== JSON.stringify(initialById.get(fee.id));
        }
        if (typeof fee.id === 'string' && fee.id.startsWith('new-')) return true;
        return !fee.id;
      });
      const createPromises = newFees.map((fee) => {
        const createData: CreateDealAdvisorFeeRequest = {
          service: getServiceValue(fee.service),
          firm: fee.firm,
          task: fee.task || null,
          currency: getCurrencyValue(fee.currency),
          // `??` so a legitimate 0 amount is saved rather than nulled.
          quote_amount: fee.quote_amount ?? null,
          actual_amount: fee.actual_amount ?? null,
          billable_hours_total_amount: fee.billable_hours_total_amount ?? null,
          accordion_id: accordionIdOf(fee),
        };
        return apiPost(`deal-advisor-fees/${dealId}`, createData).catch(
          (): ApiResponse => ({
            success: false,
            status: 0,
            message: 'Failed to create advisor fee. Please try again.',
          }),
        );
      });

      const [updateResults, createResults] = await Promise.all([
        Promise.all(updatePromises),
        Promise.all(createPromises),
      ]);
      const failedResponse = [...updateResults, ...createResults].find(
        (response) => !response?.success,
      );

      if (failedResponse) {
        // Swap temp ids for real ids on fees that were created, so a retry
        // doesn't create duplicates.
        const createdIds = new Map<string, number>();
        createResults.forEach((response, index) => {
          const createdId = (response?.data as { id?: unknown } | undefined)?.id;
          const tempId = newFees[index].id;
          if (response?.success && typeof createdId === 'number' && typeof tempId === 'string') {
            createdIds.set(tempId, createdId);
          }
        });
        if (createdIds.size > 0) {
          setFees((prevFees) =>
            prevFees.map((fee) =>
              typeof fee.id === 'string' && createdIds.has(fee.id)
                ? { ...fee, id: createdIds.get(fee.id) as number, isPlaceholder: false }
                : fee
            )
          );
        }
        // Stay in edit mode and keep the user's edits so they can retry.
        showErrorFromResponse(failedResponse);
        return;
      }

      // Refresh data
      const response = await apiGet<DealAdvisorFee[]>(`deal-advisor-fees/${dealId}`);
      if (response && response.success && response.data) {
        const rows = mapApiFeesToRows(response.data);
        setFees(rows);
        setInitialFees(structuredClone(rows));
        setEditMode(false);
        setDirtyGrid(false);
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error('Failed to save advisor fees:', error);
      showErrorFromResponse({
        success: false,
        status: 0,
        message: 'Failed to save advisor fees. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const handleDeleteRow = async (feeId: number) => {
    if (!confirm('Are you sure you want to delete this advisor fee?')) {
      return;
    }

    try {
      const response = await apiDelete(`deal-advisor-fees`, feeId);
      if (response && response.success) {
        // Remove from local state
        setFees((prevFees) => prevFees.filter((fee) => fee.id !== feeId));
        setInitialFees((prevFees) => prevFees.filter((fee) => fee.id !== feeId));
      }
    } catch (error) {
      console.error('Failed to delete advisor fee:', error);
    }
  };

  const handleAddRow = () => {
    setEditMode(true);
    const newRow = {
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      deal_id: dealId,
      service: undefined,
      accordion: null,
      firm: '',
      task: null,
      currency: null,
      quote_amount: null,
      actual_amount: null,
      billable_hours_total_amount: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as unknown as DealAdvisorFeeRow;
    setFees((prev) => [...prev, newRow as any]);
  };

  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '',
      field: 'delete',
      width: 50,
      editable: false,
      cellRenderer: (params: ICellRendererParams) => {
        // Only show delete for saved rows (numeric IDs)
        if (!params.data?.id || typeof params.data.id !== 'number') {
          return null;
        }
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRow(params.data.id);
            }}
            sx={{ padding: '2px' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    
    createSingleSelectColumn('service', 'Service', serviceTypes, 'service', 200, editMode),
    ...(accordionOptions.length > 0
      ? [
          {
            ...createSingleSelectColumn(
              'accordion',
              'Add-On',
              accordionOptions,
              'accordion_name',
              170,
              editMode,
              true,
              WHOLE_DEAL_LABEL,
            ),
            valueFormatter: ({ value }: { value: any }) =>
              value?.id != null
                ? (accordionOptions.find((a) => a.id === value.id)?.accordion_name ??
                  value.accordion_name ??
                  '')
                : WHOLE_DEAL_LABEL,
          },
        ]
      : []),
  
    {
      field: 'firm',
      headerName: 'Firm',
      editable: editMode,
      width: 250,
    },
    {
      field: 'task',
      headerName: 'Task',
      editable: editMode,
      width: 300,
    },
    createSingleSelectColumn('currency', 'Currency', currencyOptions, 'currency', 120, editMode),

    {
      field: 'quote_amount',
      headerName: 'Quote Amount',
      editable: editMode,
      width: 150,
      valueFormatter: numberFormatter,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
      },
    },
    {
      field: 'actual_amount',
      headerName: 'Actual Amount',
      editable: editMode,
      width: 150,
      valueFormatter: numberFormatter,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
      },
    },
    {
      field: 'billable_hours_total_amount',
      headerName: 'Billable Hours Total',
      editable: editMode,
      width: 180,
      valueFormatter: numberFormatter,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
      },
    },
  ], [editMode, serviceTypes, currencyOptions, accordionOptions]);

  // In-closing slices (the deal and its add-ons) still missing standard quotes,
  // from the saved fees.
  const missingQuotes = useMemo(() => {
    const saved = initialFees.filter((fee) => typeof fee.id === 'number');
    const slices: Array<{ label: string; accordionId: number | null }> = [];
    if (statusIsInClosing(dealStatus)) {
      slices.push({ label: 'Deal', accordionId: null });
    }
    for (const accordion of accordions) {
      if (accordion.accordion_type === 'pipeline' && statusIsInClosing(accordion.status)) {
        slices.push({ label: accordion.accordion_name, accordionId: accordion.id });
      }
    }
    return slices
      .map(({ label, accordionId }) => ({
        label,
        missing: REQUIRED_ADVISOR_SERVICES.filter(
          (service) =>
            !saved.some(
              (fee) =>
                (fee.accordion?.id ?? null) === accordionId &&
                sameService(serviceName(fee.service), service) &&
                fee.quote_amount != null,
            ),
        ),
      }))
      .filter((slice) => slice.missing.length > 0);
  }, [accordions, dealStatus, initialFees]);

  return (
    <>
      {missingQuotes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This deal is in closing. Please add advisor fee quotes for:{' '}
          {missingQuotes
            .map((slice) => `${slice.label}: ${slice.missing.join(', ')}`)
            .join('; ')}
          .
        </Alert>
      )}
      <div className="ag-theme-alpine" style={{ height: 400, width: '100%', marginTop: 0 }}>
        <AgGridReact
          {...gridEditProps}
          ref={gridRef}
          rowData={fees}
          columnDefs={columnDefs}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={editMode}
          defaultColDef={{ flex: 0, resizable: true }}
          rowHeight={30}
          rowClassRules={{ 'advisor-fee-row--placeholder': ({ data }) => !!data?.isPlaceholder }}
          getRowId={({ data }) => {
            return data?.id?.toString() || `fallback-${Math.random().toString(36).substr(2, 9)}`;
          }}
        />
      </div>
      
      <Box display="flex" gap={2} mb={1} mt={2}>
        {editMode ? (
          <>
            <CustomButton
              text="Cancel"
              handleButtonPress={handleCancelButtonPress}
              isButtonDisabled={isSaving}
              solid={false}
            />
            <CustomButton
              text="Save"
              handleButtonPress={requestSave}
              isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
              isLoading={isSaving}
              isLoadingText="Saving"
              solid={true}
            />
          </>
        ) : (
          <CustomButton
            text="Edit"
            handleButtonPress={() => {
              if (!canEdit) return;
              setEditMode(true);
            }}
            isButtonDisabled={!canEdit || editMode}
            solid={false}
          />
        )}
        <CustomButton
          text="Add Row"
          handleButtonPress={handleAddRow}
          isButtonDisabled={!canEdit || isSaving}
          solid={false}
        />
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
};

export default DealAdvisorFees;

